<template>
	<Modal v-model:visible="visible" :title="$t('settings.sub-networks.' + i18nPrefix + '.header')" @ok="onOk" @cancel="onCancel">
		<Form
			:model="formState"
			:label-col="{ span: 12 }"
			:wrapper-col="{ span: 12 }"
			:colon="false"
		>
			<FormItem
				:label="$t('settings.sub-networks.' + i18nPrefix + '.labelLabel')"
				name="label"
				v-bind="validateInfos.label"
			>
				<InputRestricted
					ref="labelField"
					v-model="formState.label"
					:allowedFormatChecker="mountpointChecker"
					:toolTipText="$t('validators.mountpointMessage')"
					@keyup.enter="onOk"
				/>
			</FormItem>
			<FormItem
				:label="$t('settings.sub-networks.' + i18nPrefix + '.titleLabel')"
				name="title"
				v-bind="validateInfos.title"
			>
				<Input v-model:value="formState.title" @keyup.enter="onOk" />
			</FormItem>
			<FormItem :label="$t('settings.sub-networks.' + i18nPrefix + '.fullMountpointLabel')">
				<div class="pd-sm-x">{{ fullMountpointLabel }}</div>
			</FormItem>
		</Form>
		<template #footer>
			<Button key="back" @click="onCancel">{{ $t('settings.sub-networks.' + i18nPrefix + '.cancelButtonLabel') }}</Button>
			<Button key="submit" type="primary" :loading="isSubmitting" :disabled="!isChanged" @click="onOk">{{ $t('settings.sub-networks.' + i18nPrefix + '.okButtonLabel') }}</Button>
		</template>
	</Modal>
</template>

<script>
import { reactive, ref, nextTick, toRaw, watch, computed, toRef } from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { Modal, Form, FormItem, Input, Button, message } from 'ant-design-vue';

import Api from '@/api/Api';
import Validators from '@/api/Validators';

import { applyFieldErrors, runValidatedSubmit } from '@/components/common/formModal';

import InputRestricted from '@/components/common/InputRestricted';

export default {
	components: {
		Modal, Form, FormItem, InputRestricted, Input, Button,
	},
	props: {
		addMode: Boolean,
		subNetworkId: Number,
		subNetworkLabel: String,
		subNetworkTitle: String,
		baseMountpoint: String,
		modelValue: Boolean,
		onSuccess: Function,
		fetchSettings: Function,
	},
	emits: ['update:modelValue'],
	setup(props, { emit }) {
		const { t } = useI18n();

		const visible = useVModel(props, 'modelValue', emit);

		const formState = reactive({
			label: '',
			title: '',
		});

		const {
			resetFields,
			validate,
			validateInfos,
		} = Validators.useAddSubNetworkFormRules(formState);

		const isSubmitting = ref(false);
		const labelField = ref(null);
		
		const addModeRef = toRef(props, 'addMode');
		const i18nPrefix = computed(() => addModeRef.value ? 'add-modal' : 'rename-modal');
		const subNetworkIdRef = toRef(props, 'subNetworkId');
		const subNetworkLabelRef = toRef(props, 'subNetworkLabel');
		const subNetworkTitleRef = toRef(props, 'subNetworkTitle');
		
		watch(visible, (value) => {
			if (value) {
				// очищаем поля при открытии попапа
				resetFields();
				formState.label = addModeRef.value ? '' : subNetworkLabelRef.value;
				formState.title = addModeRef.value ? '' : subNetworkTitleRef.value;
				isSubmitting.value = false;
				nextTick(() => {
					if (labelField.value?.focus) {
						labelField.value.focus();
					}
				});
			}
		});

		const isChanged = computed(() => {
			return formState.label !== subNetworkLabelRef.value || formState.title !== subNetworkTitleRef.value;
		});

		const onOk = () => {
			if (!isChanged.value) {
				return;
			}
			runValidatedSubmit(validate, isSubmitting, () => {
				const id = addModeRef.value ? 0 : subNetworkIdRef.value;
				Api.subNetworkCreateRename(id, toRaw(formState), response => {
					if (response.status === 'ok') {
						message.success(t('settings.sub-networks.' + i18nPrefix.value + '.success'));
						visible.value = false;
						props.fetchSettings(() => props.onSuccess(response.id));
					} else {
						applyFieldErrors(validateInfos, response.errors, ['label', 'title']);
					}
					isSubmitting.value = false;
				});
			});
		};

		const onCancel = () => {
			visible.value = false;
		};

		const fullMountpointLabel = computed(() => {
			return props.baseMountpoint + '_' + formState.label;
		});

		return {
			visible,
			i18nPrefix,
			formState,
			validateInfos,
			isSubmitting,
			mountpointChecker: Validators.mountpointChecker,
			labelField,
			fullMountpointLabel,
			isChanged,
			onOk,
			onCancel,
		};
	},
};
</script>
